/**
 * Grid bookcase (family 1, research/taxonomy.md §1.1): a closed case of two full-height
 * sides, a top and a bottom, 1–4 bays separated by full-height dividers, each bay with its
 * own fixed shelves; back none, overlay or rebated; optional plinth.
 */
import { frontsParameterDef } from '../fronts';
import { baseParameterDefs } from '../base';
import type { FamilyDefinition } from '../types';
import { mm } from '../../domain/units';

export const GRID_DEFINITION: FamilyDefinition = Object.freeze({
  id: 'grid-bookcase',
  version: '1.0',
  label: 'Grid bookcase',
  description: 'A closed case with full-height sides and one or more bays of fixed shelves.',
  aliases: ['single-bay-open-bookcase'],
  parameters: [
    { key: 'W', type: 'length', fieldKey: 'W', range: { min: mm(400), max: mm(2400), step: 10 }, default: mm(800), unit: 'mm', critical: true, ui: { label: 'Width', control: 'field', group: 'Overall size' } },
    { key: 'H', type: 'length', fieldKey: 'H', range: { min: mm(600), max: mm(2200), step: 10 }, default: mm(1200), unit: 'mm', critical: true, ui: { label: 'Height', control: 'field', group: 'Overall size' } },
    { key: 'D', type: 'length', fieldKey: 'D', range: { min: mm(200), max: mm(450), step: 10 }, default: mm(300), unit: 'mm', critical: true, ui: { label: 'Depth', help: 'Includes the back.', control: 'field', group: 'Overall size' } },
    {
      key: 'bays', type: 'count', fieldKey: 'bays', range: { min: 1, max: 4, step: 1 }, default: 1, unit: 'count', critical: false,
      ui: { label: 'Bays', help: 'Bays side by side, separated by full-height dividers. Changing the count makes them equal widths.', control: 'stepper', group: 'Bays' },
    },
    {
      key: 'bayWidths', type: 'perBay', fieldKey: 'bayWidths', default: null, unit: 'mm', critical: false,
      ui: { label: 'Bay widths', help: 'Clear width of each bay, left to right. The bay beside the one you change adjusts.', control: 'rows', group: 'Bays', showWhen: { key: 'bays', notEquals: 1 } },
      behaviours: ['even', 'rebalance'],
    },
    {
      key: 'shelfCount', type: 'count', fieldKey: 'shelfCount', range: { min: 0, max: 8, step: 1 }, default: 3, unit: 'count', critical: false,
      ui: { label: 'Shelves', help: 'Changing the count spaces the openings evenly.', control: 'stepper', group: 'Shelves' },
    },
    {
      key: 'openings', type: 'perOpening', fieldKey: 'openings', default: null, unit: 'mm', critical: false,
      ui: { label: 'Openings', help: 'Clear height, listed top to bottom.', control: 'rows', group: 'Shelves' },
      behaviours: ['even', 'rebalance', 'remainder'],
    },
    {
      key: 'back', type: 'enum', fieldKey: 'back', default: 'overlay', critical: false,
      options: [
        { value: 'overlay', label: 'Overlay', help: 'Screwed over the back edges; its edges show at the sides.' },
        { value: 'rebated', label: 'Rebated', help: 'Set into a rebate in the sides, top and bottom.' },
        { value: 'none', label: 'None', help: 'Open back: fix the case to the wall to stop racking.' },
      ],
      ui: { label: 'Back', control: 'choice', group: 'Construction' },
    },
    // what the case stands on (FND-08; sides to the floor, pilot-v1-r2 p01): the shared base controls
    ...baseParameterDefs('Construction').map((p) => (p.key === 'plinth' ? { ...p, fieldKey: 'plinth' } : p)),
    {
      key: 'joinery', type: 'enum', default: 'screwed-butt', critical: false,
      options: [
        { value: 'screwed-butt', label: 'Screwed', help: 'Shelves butt between the sides, glued and screwed.' },
        { value: 'housed-dado', label: 'Housed', help: 'Shelves sit in housings one third of the thickness deep.' },
      ],
      ui: { label: 'Shelf joints', control: 'choice', group: 'Construction' },
    },
    frontsParameterDef(),
    {
      key: 'stock', type: 'enum', fieldKey: 'stockThickness', default: 'birch-ply-18', critical: true,
      options: [
        { value: 'birch-ply-18', label: 'Birch plywood 18 mm' },
        { value: 'sheesham-board-25', label: 'Sheesham 25 mm' },
        { value: 'oak-board-20', label: 'Oak 20 mm' },
      ],
      ui: { label: 'Material', help: 'Chosen in Look. Solid timber wider than the boards is glued up.', control: 'choice', group: 'Construction', step: 'look' },
    },
  ],
  presets: [
    { id: 'even', label: 'Even 800 × 1200', description: '800 × 1200 × 300, 3 shelves', params: { W: mm(800), H: mm(1200), D: mm(300), shelfCount: 3 } },
    { id: 'residual', label: 'Residual 800 × 1200, 6 shelves', description: '800 × 1200 × 300, 6 shelves', params: { W: mm(800), H: mm(1200), D: mm(300), shelfCount: 6 } },
    { id: 'hylla', label: 'Hylla (uneven)', description: '900 × 1800 × 320, 4 shelves, uneven', params: { W: mm(900), H: mm(1800), D: mm(320), shelfCount: 4 } },
    { id: 'multibay', label: 'Three bays 1800 × 1400', description: '1800 × 1400 × 300, 3 bays, 40 mm plinth', params: { W: mm(1800), H: mm(1400), D: mm(300), bays: 3, shelfCount: 3, plinth: mm(40) } },
  ],
  handles: [{ partRole: 'shelf', parameterKey: 'openings', axis: 'y' }],
}) as FamilyDefinition;
